import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Container, Row, Col, Card, Table, Form, Button, Spinner, Alert, Badge, Modal } from 'react-bootstrap'
import client from '../../api/client'

function PollList() {
  const navigate = useNavigate()
  const [polls, setPolls] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [filters, setFilters] = useState({
    active: '',
    context_type: ''
  })
  const [deleteModal, setDeleteModal] = useState({ show: false, poll: null })

  useEffect(() => {
    fetchPolls()
  }, [page, filters])

  const fetchPolls = async () => {
    setLoading(true)
    try {
      const params = {
        page,
        per_page: 20,
        ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v !== ''))
      }
      const response = await client.get('/admin/api/v1/polls', { params })
      setPolls(response.data.polls || [])
      setTotal(response.data.total || 0)
    } catch (err) {
      console.error('Error fetching polls:', err)
      setError('Erreur lors du chargement des sondages')
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async () => {
    if (!deleteModal.poll) return
    try {
      await client.delete(`/admin/api/v1/polls/${deleteModal.poll.id}`)
      setSuccess(`Sondage "${deleteModal.poll.title}" supprimé`)
      setDeleteModal({ show: false, poll: null })
      fetchPolls()
      setTimeout(() => setSuccess(''), 3000)
    } catch (err) {
      console.error('Error deleting poll:', err)
      setError('Erreur lors de la suppression du sondage')
      setTimeout(() => setError(''), 3000)
    }
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  const getContextLabel = (type) => {
    const labels = {
      page: 'Page',
      feature: 'Fonctionnalité',
      model: 'Modèle',
      general: 'Général'
    }
    return labels[type] || type
  }

  if (loading && polls.length === 0) {
    return (
      <Container fluid>
        <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Chargement...</span>
          </Spinner>
        </div>
      </Container>
    )
  }

  return (
    <Container fluid>
      <Row className="mb-4">
        <Col>
          <h2>📊 Sondages</h2>
          <p className="text-muted">Gestion des sondages et enquêtes utilisateurs</p>
        </Col>
        <Col className="text-end">
          <Button variant="primary" onClick={() => navigate('/polls/new')}>
            + Créer un sondage
          </Button>
        </Col>
      </Row>

      {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert variant="success" dismissible onClose={() => setSuccess('')}>{success}</Alert>}

      <Card className="mb-3">
        <Card.Body>
          <Row>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Statut</Form.Label>
                <Form.Select
                  value={filters.active}
                  onChange={(e) => setFilters({ ...filters, active: e.target.value })}
                  size="sm"
                >
                  <option value="">Tous</option>
                  <option value="true">Actifs</option>
                  <option value="false">Inactifs</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Type de contexte</Form.Label>
                <Form.Select
                  value={filters.context_type}
                  onChange={(e) => setFilters({ ...filters, context_type: e.target.value })}
                  size="sm"
                >
                  <option value="">Tous</option>
                  <option value="page">Page</option>
                  <option value="feature">Fonctionnalité</option>
                  <option value="model">Modèle</option>
                  <option value="general">Général</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card>
        <Card.Body>
          <Table responsive hover>
            <thead>
              <tr>
                <th>Titre</th>
                <th>Contexte</th>
                <th>Questions</th>
                <th>Réponses</th>
                <th>Dates</th>
                <th>Statut</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {polls.map((poll) => (
                <tr key={poll.id}>
                  <td>
                    <strong>{poll.title}</strong>
                    {poll.description && (
                      <>
                        <br />
                        <small className="text-muted">{poll.description.substring(0, 60)}...</small>
                      </>
                    )}
                  </td>
                  <td>
                    <Badge bg="info">{getContextLabel(poll.context_type)}</Badge>
                    {poll.context_value && (
                      <>
                        <br />
                        <small className="text-muted">{poll.context_value}</small>
                      </>
                    )}
                  </td>
                  <td className="text-center">
                    <Badge bg="secondary">{poll.question_count || 0}</Badge>
                  </td>
                  <td className="text-center">
                    <Badge bg="primary">{poll.response_count || 0}</Badge>
                  </td>
                  <td>
                    <small>
                      {poll.starts_at && `Début: ${formatDate(poll.starts_at)}`}
                      {poll.starts_at && poll.ends_at && <br />}
                      {poll.ends_at && `Fin: ${formatDate(poll.ends_at)}`}
                      {!poll.starts_at && !poll.ends_at && '-'}
                    </small>
                  </td>
                  <td>
                    <Badge bg={poll.active ? 'success' : 'secondary'}>
                      {poll.active ? 'Actif' : 'Inactif'}
                    </Badge>
                  </td>
                  <td>
                    <div className="d-flex gap-1">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => navigate(`/polls/${poll.id}/edit`)}
                      >
                        Modifier
                      </Button>
                      <Button
                        variant="outline-info"
                        size="sm"
                        onClick={() => navigate(`/polls/${poll.id}/results`)}
                      >
                        Résultats
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => setDeleteModal({ show: true, poll })}
                      >
                        Supprimer
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>

          {polls.length === 0 && (
            <div className="text-center py-4 text-muted">
              Aucun sondage trouvé
            </div>
          )}

          {total > 20 && (
            <div className="d-flex justify-content-between align-items-center mt-3">
              <span className="text-muted">
                Page {page} sur {Math.ceil(total / 20)}
              </span>
              <div>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  disabled={page === 1}
                  onClick={() => setPage(page - 1)}
                  className="me-2"
                >
                  Précédent
                </Button>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  disabled={page >= Math.ceil(total / 20)}
                  onClick={() => setPage(page + 1)}
                >
                  Suivant
                </Button>
              </div>
            </div>
          )}
        </Card.Body>
      </Card>

      <Modal show={deleteModal.show} onHide={() => setDeleteModal({ show: false, poll: null })}>
        <Modal.Header closeButton>
          <Modal.Title>Confirmer la suppression</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Êtes-vous sûr de vouloir supprimer le sondage "{deleteModal.poll?.title}" ?
          Cette action supprimera également toutes les questions et réponses associées.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDeleteModal({ show: false, poll: null })}>
            Annuler
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Supprimer
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  )
}

export default PollList
